<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorValidations} from '../../../kp-value-editor/types';
    import type {AcceptableRootValueEditorOptions, Childrenable} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {AcceptableRootValueEditorLocalizations} from './types';
    import type {TreeNode, TreeNodeEventPayload, TreeState} from './tree-control-utils/types';
    import type {TreeNodeWithChildren} from './tree-control-utils/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {onDestroy, onMount, setContext} from 'svelte';
    import {createInputDescription} from '../../description/utils';
    import {getContext} from 'svelte';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {style} from 'svelte-forms';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {writable} from 'svelte/store';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import {FOCUS_OUTSIDE_EVENT_TYPE, focusOutside} from '../multiple-acceptable/use.focus-outside';
    import TreeNodeComponent from './tree-control-utils/TreeNode.svelte';
    import Description from '../../description/Description.svelte';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {
        createTreeNode,
        flattenTree,
        findFirstVisibleNode,
        findLastVisibleNode,
        findNextVisibleNode,
        findParentNodeInSubtree,
        findPreviousVisibleNode
    } from './tree-control-utils/utils';
    import {
        getOptionById,
        getOptionIdResolver,
        identifierToModelParser,
        modelToIdentifierFormatter,
        validatorFactory
    } from './utils';

    type MODEL_VALUE = $$Generic<Childrenable<MODEL_VALUE>>;

    export let model: MODEL_VALUE[];
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string; // unsupported

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: AcceptableRootValueEditorOptions<MODEL_VALUE>;
    export let validations: ValueEditorValidations;

    ignoreUnusedProperties(placeholder);

    model = normalizeModelIfNeeded(model);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withOptions(formControlOptions)
        .withValidators([...validatorFactory(validations)])
        .withParsers([identifierToModelParser(() => options.acceptableValue, options)])
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([modelToIdentifierFormatter(options), emptyAsNullFormatter(options, [])])
        .build(getLogger());

    const localize: EditorsLocalizationFunction<AcceptableRootValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});
    const labelContext = getValueEditorLabelContext();

    $: model = normalizeModelIfNeeded(model);

    const modelValueSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    let currentViewValue: (string | number)[];
    const viewValueSubscription = formControl.getViewValue$().subscribe((value) => currentViewValue = value);

    let rootTreeNode: TreeNodeWithChildren;
    $: rootTreeNode = createTreeNode(options.acceptableValue, options.disabledItems, isDisabled, getOptionIdResolver(options));

    let treeContainerElementRef: HTMLElement;

    const labelClicksSubscription = labelContext?.clickEvent$?.subscribe(() => {
        const nodeElements = treeContainerElementRef.querySelectorAll<HTMLElement>('.node-content');
        const focusableNode: HTMLElement = [...nodeElements].find((element: HTMLElement) => element.tabIndex === 0);
        focusableNode?.focus();
    });

    const checkedNodesIds$ = writable(currentViewValue);
    $: checkedNodesIds$.set(currentViewValue);

    const expandedNodes$ = writable<TreeNode[]>([]);
    const focusedNode$ = writable<TreeNode>();
    setContext<TreeState<string | number>>('tree-state', {checkedNodesIds$, expandedNodes$, focusedNode$});

    onMount(() => {
        if (isFocused) {
            $focusedNode$ = rootTreeNode;
        }
    });

    onDestroy(() => {
        labelClicksSubscription?.unsubscribe();
        modelValueSubscription.unsubscribe();
        viewValueSubscription.unsubscribe();
    });

    function normalizeModelIfNeeded(modelValue: typeof model | null) {
        if (!Array.isArray(modelValue)) {
            if (options.emptyAsNull) {
                modelValue = null;
            } else {
                modelValue = [];
            }
        } else {
            if (options.emptyAsNull && modelValue.length === 0) {
                modelValue = null;
            }
        }
        return modelValue;
    }

    function handleAdd(event: CustomEvent<TreeNodeEventPayload>) {
        const optionIdentifiers = new Set([...currentViewValue]);
        const subTreeNodes = flattenTree(event.detail.node);
        subTreeNodes.filter((node) => !node.disabled).forEach((node) => optionIdentifiers.add(node.id));
        formControl.setViewValue(Array.from(optionIdentifiers));
    }

    function handleRemove(event: CustomEvent<TreeNodeEventPayload>) {
        const optionIdentifiers = new Set([...currentViewValue]);
        const subTreeNodes = flattenTree(event.detail.node);
        subTreeNodes.filter((node) => !node.disabled).forEach((node) => optionIdentifiers.delete(node.id));
        formControl.setViewValue(Array.from(optionIdentifiers));
    }

    function handleFocusFirstNode(event: CustomEvent<TreeNodeEventPayload>) {
        $focusedNode$ = findFirstVisibleNode(rootTreeNode, event.detail.node, $expandedNodes$);
    }

    function handleFocusLastNode(event: CustomEvent<TreeNodeEventPayload>) {
        $focusedNode$ = findLastVisibleNode(rootTreeNode, event.detail.node, $expandedNodes$);
    }

    function handleFocusPreviousNode(event: CustomEvent<TreeNodeEventPayload>) {
        $focusedNode$ = findPreviousVisibleNode(rootTreeNode, event.detail.node, $expandedNodes$);
    }

    function handleFocusNextNode(event: CustomEvent<TreeNodeEventPayload>) {
        $focusedNode$ = findNextVisibleNode(rootTreeNode, event.detail.node, $expandedNodes$);
    }

    function handleFocusParentNode(event: CustomEvent<TreeNodeEventPayload>) {
        $focusedNode$ = findParentNodeInSubtree(rootTreeNode, event.detail.node);
    }

    export function getFormController(): FormControl<MODEL_VALUE[]> {
        return formControl;
    }
</script>

<div id={editorId}
     role="tree"
     aria-multiselectable="true"
     data-name={editorName}
     data-main-input
     bind:this={treeContainerElementRef}
     use:ariaLabelledby={labelContext}
     use:focusOutside
     use:touched={{formControl, touchTriggerEvent: FOCUS_OUTSIDE_EVENT_TYPE}}
     use:ariaInvalid={formControl}
     use:ariaErrormessage={formControl}
     use:ariaDescribedby={inputDescription}
     use:style={{field: formControl.getFieldStateAsStore()}}>
    <TreeNodeComponent treeNode={rootTreeNode}
                       baseId={editorId}
                       {size}
                       on:add={handleAdd}
                       on:remove={handleRemove}
                       on:focusPreviousNode={handleFocusPreviousNode}
                       on:focusNextNode={handleFocusNextNode}
                       on:focusParentNode={handleFocusParentNode}
                       on:focusFirstNode={handleFocusFirstNode}
                       on:focusLastNode={handleFocusLastNode}
                       let:node>
        <svelte:component this={options.optionLabelComponent}
                          option={getOptionById(options.acceptableValue, getOptionIdResolver(options), node.id)}>
        </svelte:component>
    </TreeNodeComponent>
</div>

<ErrorMessages formController={formControl} {forceShowErrors} {size} customClass="acceptable-root" standalone={true}/>
<Description {inputDescription}/>

<style lang="less" global>
    .has-error kp-value-editor[data-type="acceptable-root"] .error-message.acceptable-root {
        margin-top: 5px;
    }
</style>